import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@parrot-software/design-system';
import { ChevronDown as ChevronDownIcon } from '@parrot-software/design-system/icons';
import type { ColumnDefinition } from '@reports/domain/value-objects/blocks/ColumnConfig';

interface ReportTableColumnToggleProps {
  columnsConfig: ReadonlyMap<string, ColumnDefinition>;
  visibleColumns: Set<string>;
  onToggleColumn: (columnKey: string) => void;
  onToggleAllColumns: () => void;
}

/**
 * ReportTableColumnToggle - Column visibility toggle component for report tables
 *
 * Provides a dropdown menu to toggle visibility of table columns.
 * The first column is always visible and excluded from toggle options.
 * Includes a "Todas" (All) option to toggle all columns at once.
 *
 * Features:
 * - DropdownMenu toggleable columns
 * - "Todas" option to show/hide all columns simultaneously
 * - First column (index 0) is always visible and not toggleable
 *
 * @param {ReportTableColumnToggleProps} props - Component properties
 * @param {ReadonlyMap<string, ColumnDefinition>} props.columnsConfig - Map of column configurations
 * @param {Set<string>} props.visibleColumns - Set of currently visible column keys
 * @param {(columnKey: string) => void} props.onToggleColumn - Callback to toggle individual column visibility
 * @param {() => void} props.onToggleAllColumns - Callback to toggle all columns at once
 * @returns {React.ReactElement} Column toggle dropdown component
 *
 * @example
 * <ReportTableColumnToggle
 *   columnsConfig={tableConfig.columnsConfig}
 *   visibleColumns={visibleColumnsSet}
 *   onToggleColumn={handleToggleColumn}
 *   onToggleAllColumns={handleToggleAllColumns}
 * />
 */
export const ReportTableColumnToggle = ({
  columnsConfig,
  visibleColumns,
  onToggleColumn,
  onToggleAllColumns,
}: ReportTableColumnToggleProps) => {
  const { t } = useTranslation(['reports']);

  // Get all column entries and skip the first one (always visible)
  const columnEntries = Array.from(columnsConfig.entries());
  const toggleableColumns = columnEntries.slice(1);

  const allColumnsVisible = toggleableColumns.every(([key]) => visibleColumns.has(key));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="font-normal" data-testid="column-toggle-button" variant="outline">
          {t('reportCommon:table.showColumns.toggle')}
          <ChevronDownIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="pr-4">
        <DropdownMenuCheckboxItem
          checked={allColumnsVisible}
          data-testid="column-toggle-all"
          onCheckedChange={onToggleAllColumns}
        >
          {t('reportCommon:table.showColumns.toggleAll')}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {toggleableColumns.map(([key, definition]) => (
          <DropdownMenuCheckboxItem
            checked={visibleColumns.has(key)}
            data-testid={`column-toggle-${key}`}
            key={key}
            onCheckedChange={() => onToggleColumn(key)}
          >
            {t(definition.label)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
